import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { AttributionActor, Item, LabeledElement } from '../../suspectedActorsCard/types';
import { translateFn } from '../../../utils/translator.helper';
import moment from 'moment';
import { GroupAttributionIocSidePanelWebComponent } from '../../groupAttributionIocSidePanel';
import { XdrOverviewWebComponent } from '../../xdrOverview';
import { GroupAttributionMitreSidePanelWebComponent } from '../../groupAttributionMitreSidePanel';
import { GroupAttributionCveSidePanelWebComponent } from '../../groupAttributionCveSidePanel';
import { IntelliZoneIntegrationData } from '../../../utils/xdrOverview/intellizone/intellizone.types';

/**
 * @tag actor-card
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-actor-card')
export class ActorCardWebComponent extends LitElement {
  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  actor: AttributionActor;

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  intelliZoneIntegrationData: IntelliZoneIntegrationData;

  @property({ type: Object })
  formattedActor: Item;

  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  companyId = null;

  private buttonStrings = {
    showMore: translateFn('Show More'),
    showLess: translateFn('Show Less'),
  };

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    this.formatActorData();
    return html` <div>${this.renderBody()}</div> `;
  }

  private renderBody(): TemplateResult<1> {
    return html`
      <div class="accordion-content pt-1">
        <gz-collapsable-container class="pb-1" fixedContentHeight="40" .buttonStrings="${this.buttonStrings}">
          <gz-text class="item-description" fontSize="12">${this.formattedActor.description}</gz-text>
        </gz-collapsable-container>
        ${this.formattedActor.items.map(this.renderLabeledContent)}
      </div>
    `;
  }

  private renderLabeledContent(labeledElement: LabeledElement): TemplateResult<1> {
    if (labeledElement.isLink) {
      return html`
        <gz-labeled-content label="${labeledElement.label}" labelfontsize="12">
          <gz-tooltip class="content-labeled-component" tooltipText="${labeledElement.value}" isEllipsis>
            <gz-link fontSize="12" class="d-inline" @click="${labeledElement.callback}"
              >${labeledElement.value}</gz-link
            >
          </gz-tooltip>
        </gz-labeled-content>
      `;
    }

    return html`
      <gz-labeled-content label="${labeledElement.label}" labelfontsize="12">
        <gz-tooltip class="content-labeled-component" tooltipText="${labeledElement.value}" isEllipsis>
          <gz-text fontSize="12" class="d-inline">${labeledElement.value}</gz-text>
        </gz-tooltip>
      </gz-labeled-content>
    `;
  }

  private formatActorData() {
    this.formattedActor = {
      confidence: this.actor.confidence,
      description: this.actor.description,
      id: this.actor.id,
      items: this.formatActorCardItems(),
      name: this.actor.name,
    };
  }

  /**
   * Check if requested properties exist on backend actor object.
   * @param {string} actorProperty
   * @private
   */
  private checkActorProperty(actorProperty: string): boolean {
    return this.actor.hasOwnProperty(actorProperty) && this.actor[actorProperty];
  }

  private formatActorCardItems(): Array<LabeledElement> {
    const labeledElements: Array<LabeledElement> = [];
    labeledElements.push({
      label: `${translateFn('Alias')}:`,
      value: this.checkActorProperty('aliases') ? this.actor.aliases.join(', ') : translateFn('N/A'),
    });
    labeledElements.push({
      label: `${translateFn('Motivation')}:`,
      value: this.checkActorProperty('motivation') ? this.actor.motivation.join(', ') : translateFn('N/A'),
    });
    labeledElements.push({
      label: `${translateFn('Sponsor')}:`,
      value: this.checkActorProperty('sponsor') ? this.actor.sponsor.join(', ') : translateFn('N/A'),
    });
    labeledElements.push({
      label: `${translateFn('eCrime')}:`,
      value: this.checkActorProperty('eCrimes') ? this.actor.eCrimes.join(', ') : translateFn('N/A'),
    });
    labeledElements.push({
      label: `${translateFn('First detection in company')}:`,
      value: this.checkActorProperty('firstDetected')
        ? moment(this.actor.firstDetected).format('DD MMM YYYY, HH:mm')
        : translateFn('N/A'),
    });
    labeledElements.push({
      label: `${translateFn('Targets your country')}:`,
      value: this.actor.targetsCountry ? translateFn('Yes') : translateFn('No'),
    });
    labeledElements.push({
      label: `${translateFn('Targets your industry')}:`,
      value: this.actor.targetsSector ? translateFn('Yes') : translateFn('No'),
    });
    labeledElements.push({
      label: `${translateFn('Involved in')}:`,
      value: this.checkActorProperty('involvedInIncidents') ? `${this.actor.involvedInIncidents.length} ${translateFn('incidents')}` : translateFn('N/A'),
      isLink: true,
      callback: this.handlePivotToIncidents,
    });

    const hasCVEProperty = this.checkActorProperty('cve');
    labeledElements.push({
      label: `${translateFn('CVEs')}:`,
      value: hasCVEProperty ? this.actor.cve.length.toString() : translateFn('N/A'),
      isLink: hasCVEProperty && this.actor.cve.length > 0,
      callback: hasCVEProperty ? this.cveSidePanelCallback : null,
    });

    const hasIoCProperty = this.checkActorProperty('iocs');
    labeledElements.push({
      label: `${translateFn('IoCs')}:`,
      value: hasIoCProperty ? this.actor.iocs.length.toString() : translateFn('N/A'),
      isLink: hasIoCProperty && this.actor.iocs.length > 0,
      callback: hasIoCProperty ? this.iocSidePanelCallback : null,
    });

    const hasMitreProperty = this.checkActorProperty('mitreTechniques');
    labeledElements.push({
      label: `${translateFn('MITRE techniques:')}`,
      value: hasMitreProperty ? this.actor.mitreTechniques.length.toString() : translateFn('N/A'),
      isLink: hasMitreProperty && this.actor.mitreTechniques.length > 0,
      callback: hasMitreProperty ? this.mitreSidePanelCallback : null,
    });

    return labeledElements;
  }

  handlePivotToIncidents() {
    Application.lib.Navigation.openInNewTab(`/incidents/filter/actor/${this.companyId}&${encodeURIComponent(this.actor.name)}`);
  }

  cveSidePanelCallback() {
    const sidePanel = new GroupAttributionCveSidePanelWebComponent();
    sidePanel.companyId = this.companyId;
    sidePanel.data = this.actor.cve;
    this.openSidePanel(sidePanel);
  }

  iocSidePanelCallback() {
    const sidePanel = new GroupAttributionIocSidePanelWebComponent();
    sidePanel.companyId = this.companyId;
    sidePanel.data = this.actor.iocs;
    sidePanel.intelliZoneIntegrationData = this.intelliZoneIntegrationData;
    this.openSidePanel(sidePanel);
  }

  mitreSidePanelCallback() {
    const sidePanel = new GroupAttributionMitreSidePanelWebComponent();
    sidePanel.companyId = this.companyId;
    sidePanel.data = this.actor.mitreTechniques;
    this.openSidePanel(sidePanel);
  }

  openSidePanel(sidePanel) {
    const overview = document.querySelector('gz-xdr-overview') as XdrOverviewWebComponent;
    overview.controller.view.dom.$sideMenu.setMenuContent(sidePanel);
    overview.controller.view.dom.$sideMenu.openSideMenu();
  }

  constructor() {
    super();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'actor-card': ActorCardWebComponent;
  }
}
